import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { makeStyles, createStyles } from '@material-ui/core/styles';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography } from '@material-ui/core';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core';

import { LearnStatType } from '../../../Constant';
import { DialogAction } from '../../../function/useModal';

type LearnResultDialogProps = {
  dialog: DialogAction<LearnResultDialogResponse>;
  devIndex: number;
};

export type LearnResultDialogResponse = {};

type DEV_STATUS = {
  DEV_INDEX: number;
  COL_TIME?: Date;
  COL_MODE?: number;
  STATUS?: number;
  LEARN_STAT?: number;
  SCHEDULE_TIME?: Date;
  RETRY_CNT?: number;
  LEARN_1_V?: number;
  LEARN_1_C?: number;
  LEARN_2_V?: number;
  LEARN_2_C?: number;
  LEARN_3_V?: number;
  LEARN_3_C?: number;
  CV_V?: number;
  CV_C?: number;
};

type DevStatusModel = {
  DevStatus: DEV_STATUS;
  /**ばらつき有無指標に使用する値 */
  CoefficientThreshold: number;
  /**振動表示単位 */
  VibrationUnit: string;
  /**電流表示単位 */
  CurrentUnit: string;
};

const useStyles = makeStyles(
  createStyles({
    tableContainer: {
      maxHeight: 'calc(100vh - 300px)',
      //fontSize: '0.8em',
    },
    tableHeader: {
      borderWidth: '1px',
      borderColor: '#dcdcdc',
      borderStyle: 'solid',
      backgroundColor: 'grey',
      textAlign: 'center',
      color: 'white',
      padding: '1px',
      minWidth: '80px',
      borderCollapse: `collapse`,
    },
    tableCell: {
      borderWidth: '1px',
      borderColor: '#dcdcdc',
      borderStyle: 'solid',
      borderCollapse: 'collapse',
      textAlign: 'left',
    },
    colKind: {
      width: '100px',
    },
    colItem: {
      width: '100px',
    },
    col1st: {
      width: '100px',
    },
    col2nd: {
      width: '100px',
    },
    col3rd: {
      width: '100px',
    },
    colVarparam: {
      width: '100px',
    },
    button: {
      width: '150px',
    },
  })
);

const LearnResultDialog: React.FC<LearnResultDialogProps> = (props: LearnResultDialogProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [mode, setMode] = useState<boolean>(true);
  const [devStatus, setDevStatus] = useState<DEV_STATUS>();
  const [coefficientThreshold, setCoefficientThreshold] = useState<number>();
  const [resultMessage, setResultMessage] = useState<string>('');

  /**振動表示単位 */
  const [vibrationUnit, setVibrationUnit] = useState<string>('');
  /**電流表示単位 */
  const [currentUnit, setCurrentUnit] = useState<string>('');

  useEffect(() => {
    const url = `api/DevStatus/${props.devIndex}`;
    fetch(url)
      .then((res) => res.json())
      .then((result: DevStatusModel) => {
        console.debug(['result', result]);

        if (result.DevStatus != null) {
          setDevStatus(result.DevStatus);
        }
        if (result.CoefficientThreshold != null) {
          setCoefficientThreshold(result.CoefficientThreshold);
        }
        if (result.VibrationUnit != null) {
          setVibrationUnit(result.VibrationUnit);
        }
        if (result.CurrentUnit != null) {
          setCurrentUnit(result.CurrentUnit);
        }
        if (
          (result.DevStatus.CV_V &&
            result.DevStatus.LEARN_STAT &&
            result.DevStatus.LEARN_STAT === LearnStatType.AFTER_LEARNING_VARIATION) ||
          (result.DevStatus.CV_C &&
            result.DevStatus.LEARN_STAT &&
            result.DevStatus.LEARN_STAT === LearnStatType.AFTER_LEARNING_VARIATION)
        ) {
          setResultMessage(
            t(
              'LearnResultDialog.収集データのばらつきが大きいため，学習データを再収集してください。',
              '収集データのばらつきが大きいため，学習データを再収集してください。'
            )
          );
        }
      })
      .catch((reason) => console.log(reason));
  }, []);

  return (
    <>
      <Dialog
        open={isOpen}
        fullScreen={false}
        fullWidth={true}
        maxWidth="md"
        onExited={(e) => {
          if (mode) {
            props.dialog.apply({});
          } else {
            props.dialog.cancel();
          }
        }}
      >
        <DialogTitle>{t('LearnResultDialog.学習結果表示', '学習結果表示')}</DialogTitle>
        <DialogContent>
          <TableContainer className={classes.tableContainer}>
            <Table aria-label="judgeSetting" stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableHeader + ' ' + classes.colKind}>
                    {t('LearnResultDialog.計測種別', '計測種別')}
                  </TableCell>
                  <TableCell className={classes.tableHeader + ' ' + classes.colItem}>
                    {t('LearnResultDialog.項目種別', '項目種別')}
                  </TableCell>
                  <TableCell className={classes.tableHeader + ' ' + classes.colItem}>
                    {t('LearnResultDialog.単位', '単位')}
                  </TableCell>
                  <TableCell className={classes.tableHeader + ' ' + classes.col1st}>
                    {t('LearnResultDialog.1回目', '1回目')}
                  </TableCell>
                  <TableCell className={classes.tableHeader + ' ' + classes.col2nd}>
                    {t('LearnResultDialog.2回目', '2回目')}
                  </TableCell>
                  <TableCell className={classes.tableHeader + ' ' + classes.col3rd}>
                    {t('LearnResultDialog.3回目', '3回目')}
                  </TableCell>
                  <TableCell className={classes.tableHeader + ' ' + classes.colVarparam}>
                    {t('LearnResultDialog.変動係数', '変動係数')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell className={classes.tableCell + ' ' + classes.colKind}>
                    {t('LearnResultDialog.振動', '振動')}
                  </TableCell>
                  <TableCell className={classes.tableCell + ' ' + classes.colItem}>
                    {t('LearnResultDialog.負荷側', '負荷側')}
                  </TableCell>
                  <TableCell className={classes.tableCell + ' ' + classes.colItem}>{vibrationUnit}</TableCell>
                  <TableCell className={classes.tableCell + ' ' + classes.col1st}>
                    {(devStatus && devStatus.LEARN_1_V) ?? ''}
                  </TableCell>
                  <TableCell className={classes.tableCell + ' ' + classes.col2nd}>
                    {(devStatus && devStatus.LEARN_2_V) ?? ''}
                  </TableCell>
                  <TableCell className={classes.tableCell + ' ' + classes.col3rd}>
                    {(devStatus && devStatus.LEARN_3_V) ?? ''}
                  </TableCell>
                  <TableCell className={classes.tableCell + ' ' + classes.colVarparam}>
                    {(devStatus && devStatus.CV_V?.toExponential(4)) ?? ''}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className={classes.tableCell + ' ' + classes.colKind}>
                    {t('LearnResultDialog.電流', '電流')}
                  </TableCell>
                  <TableCell className={classes.tableCell + ' ' + classes.colItem}>
                    {t('LearnResultDialog.R相', 'R相')}
                  </TableCell>
                  <TableCell className={classes.tableCell + ' ' + classes.col1st}>{currentUnit}</TableCell>
                  <TableCell className={classes.tableCell + ' ' + classes.col1st}>
                    {(devStatus && devStatus.LEARN_1_C) ?? ''}
                  </TableCell>
                  <TableCell className={classes.tableCell + ' ' + classes.col2nd}>
                    {' '}
                    {(devStatus && devStatus.LEARN_2_C) ?? ''}
                  </TableCell>
                  <TableCell className={classes.tableCell + ' ' + classes.col3rd}>
                    {' '}
                    {(devStatus && devStatus.LEARN_3_C) ?? ''}
                  </TableCell>
                  <TableCell className={classes.tableCell + ' ' + classes.colVarparam}>
                    {(devStatus && devStatus.CV_C?.toExponential(4)) ?? ''}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <div>{resultMessage}</div>
        </DialogContent>
        <DialogActions>
          <Button
            className={classes.button}
            style={{ marginRight: '30px' }}
            color="primary"
            variant="contained"
            onClick={(e) => {
              setIsOpen(false);
              setMode(true);
            }}
          >
            {t('LearnResultDialog.閉じる', '閉じる')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LearnResultDialog;
